import { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import { useHasClickedOutside } from "lib/hooks/use-has-clicked-outside";
import { PlusSvg } from "components/decorative/plus-svg";
import { Button } from "components/buttons/button";
import { labelClassNames } from "components/forms/input";

export interface MultiSelectData<T> {
  key: string | number;
  label: string;
  rankOrder?: number;
  data: T;
}

interface Props<T> {
  label: string;
  values: MultiSelectData<T>[];
  placeholder: string;
  notFoundContent: React.ReactNode;
  setValues: Dispatch<SetStateAction<MultiSelectData<T>[]>>;
  onSearch: (searchString: String) => void;
  searchResults: MultiSelectData<T>[];
  id?: string;
  singleSelect?: boolean;
  className?: string;
  addNew?: (value: String) => Promise<MultiSelectData<T>>;
}

export function MultiSelect<T>({
  label,
  values,
  placeholder,
  notFoundContent,
  setValues,
  onSearch,
  searchResults,
  id,
  singleSelect = false,
  className,
  addNew,
}: Props<T>) {
  const [inputHasFocus, setInputHasFocus] = useState<boolean>(false);
  const componentRef = useRef(null);
  const inputRef = useRef(null);
  const hasClickedOutside = useHasClickedOutside(componentRef);
  const [searchValue, setSearchValue] = useState<string>("");
  const innerId = id ?? label.replace(" ", "");

  const addValue = (v: MultiSelectData<T>) => {
    setValues(() => {
      let arr;
      if (singleSelect) {
        arr = [];
      } else {
        arr = Boolean(values) ? [].concat(values) : [];
      }
      arr.push(v);
      return arr;
    });
  };

  const removeValue = (v: MultiSelectData<T>) => {
    setValues(() => {
      let arr = Boolean(values) ? [].concat(values) : [];
      let i = arr.findIndex((inner) => inner === v);
      arr.splice(i, 1);
      return arr;
    });
  };

  useEffect(() => {
    if (hasClickedOutside) {
      setInputHasFocus(false);
    }
  }, [hasClickedOutside]);

  return (
    <fieldset ref={componentRef} className={`flex flex-col ${className}`}>
      <label
        className={`flex flex-col relative ${labelClassNames}`}
        htmlFor={`${id}-multiselect`}>
        {label}
      </label>
      <section className='relative'>
        <div
          className={`mt-1 pl-3 pr-3 py-2 text-base border border-gray-300 focus:outline-none sm:text-sm rounded-md w-full flex flex-col bg-white z-20 focus-within:ring-green-default focus-within:outline-none focus-within:border-green-default`}
          onClick={() => inputRef.current.focus()}>
          <div className='my-auto mr-0 flex flex-row flex-wrap'>
            {values?.length > 0 &&
              values.map((v) => (
                <div
                  key={`key_${v.key}`}
                  className='rounded-sm bg-blueGray-200 border border-white box-border bg-opacity-70 flex flex-row'>
                  <span className='pl-1 whitespace-nowrap'>{v.label}</span>
                  <button
                    className='px-1 ml-1 inline text-coolGray-500 hover:text-black'
                    onClick={(e) => {
                      e.preventDefault();
                      removeValue(v);
                    }}>
                    x
                  </button>
                </div>
              ))}
          </div>
          <input
            ref={inputRef}
            className='flex-1 border-none focus:border-none focus:ring-0 p-0'
            type='search'
            id={`${id}-multiselect`}
            autoComplete='off'
            autoCorrect='off'
            placeholder={
              !!placeholder && values?.length === 0 ? placeholder : undefined
            }
            value={searchValue}
            onChange={(e) => {
              setSearchValue(e.target.value);
              if (e.target.value.trim() !== "") {
                onSearch(e.target.value);
              }
            }}
            onFocus={() => setInputHasFocus(true)}
          />
        </div>
        {((searchResults && searchResults.length > 0) ||
          values?.length > 0 ||
          !!addNew) &&
          inputHasFocus && (
            <section className='absolute bg-white top-full flex flex-col border  rounded-md border-green-default w-full shadow-lg z-10'>
              {searchResults?.length > 0
                ? searchResults.map((r) => {
                    const { key, label } = r;
                    const isChecked = values?.some(
                      (existingValue) => existingValue.key === r.key
                    );
                    return (
                      <label
                        key={`key_${key}`}
                        htmlFor={key as string}
                        className={`${
                          isChecked ? "bg-blueGray-200" : ""
                        } hover:bg-blueGray-200 hover:bg-opacity-50 px-2 py-1 cursor-pointer`}>
                        <input
                          className='sr-only'
                          type='checkbox'
                          name={key as string}
                          id={key as string}
                          checked={isChecked}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSearchValue("");
                              addValue(r);
                            } else if (!e.target.checked) {
                              removeValue(r);
                            }
                            inputRef.current.focus();
                          }}
                        />
                        {label}
                      </label>
                    );
                  })
                : searchValue !== "" &&
                  searchResults &&
                  searchResults.length === 0 && (
                    <>
                      <span className='flex justify-around pt-2 pb-1'>
                        {notFoundContent || "No Results"}
                      </span>
                    </>
                  )}
              {!!addNew && searchValue.trim() !== "" && (
                <section className='px-2  flex justify-center border-t border-white bg-gray-100'>
                  <Button
                    className='flex flex-row w-full pb-2 pt-2'
                    onClick={async () => {
                      addValue(await addNew(searchValue));
                      setSearchValue("");
                    }}>
                    <PlusSvg className='w-5 mr-2' /> Create this Tag
                  </Button>
                </section>
              )}
            </section>
          )}
      </section>
    </fieldset>
  );
}
